import { APP_INITIALIZER, ErrorHandler, NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";

import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app-routing.module";
import { HttpClientModule, HTTP_INTERCEPTORS } from "@angular/common/http";
import { DatePipe } from "@angular/common";
import { SVG_ICON_NAMES } from "@kells/shared-assets";
import {
  apiEnvironment,
  ClinicOneEnvironmentsModule,
} from "@kells/clinic-one/environments";
import { AuthModule } from "@auth0/auth0-angular";
import { AuthConfig } from "./auth.config";
import { IconRegistryService } from "@kells/utils/angular";
import { DataAccessApisModule } from "@kells/apis/data-access";
import {
  AnalyticsService,
  createPosthogWrappedErrorHandler,
} from "@kells/apis/analytics";
import { NgLetModule } from "@ngrx-utils/store";
import { StoreModule } from "@ngrx/store";
import * as Sentry from "@sentry/angular-ivy";

import { register } from "swiper/element/bundle";
import { SharedModule } from "./shared/shared.module";
import { Router } from "@angular/router";
import { PortalHttpInterceptor, RootAuthHttpInterceptor } from "./interceptors";
import { VirtualCheckupHandoffInterceptor } from "./interceptors/virtual-checkup-handoff.interceptor";
import { VirtualCheckupHandoffAuthErrorInterceptor } from "./interceptors/virtual-checkup-handoff-auth-error.interceptor";
import { SSOAuthInterceptor } from "./auth/interceptors";
import { HaleonAuthInterceptor } from "./haleon/interceptors/haleon-auth.interceptor";
// register Swiper custom elements
register();

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    NgLetModule,
    AppRoutingModule,
    HttpClientModule,
    DataAccessApisModule,
    ClinicOneEnvironmentsModule,
    SharedModule,
    AuthModule.forRoot({
      domain: AuthConfig.domain,
      clientId: AuthConfig.clientId,
      scope: AuthConfig.scope,
      // the audience field needs to be configured correctly to obtain tokens
      // that our backend recognizes.
      audience: AuthConfig.audience,
      httpInterceptor: {
        allowedList: [
          `https://sandbox.optiopublishing.com/*`,

          {
            uriMatcher: (uri: string) => {
              return (
                uri.startsWith(apiEnvironment.apiBaseUrl) &&
                // To support our SSO auth0 http interceptor should be disabled for certain routes
                !uri.startsWith(
                  `${apiEnvironment.apiBaseUrl}/${apiEnvironment.apiVersion}/auth`
                )
              );
            },
            // When SSO is completed, the user is authenticated and the auth0 http interceptor should be disabled
            // This is to prevent the user from being redirected to the auth0 login page, when auth0 cannot detect the access token
            allowAnonymous: true,
          },
        ],
      },
    }),
    StoreModule.forRoot([]),
  ],
  providers: [
    DatePipe,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: VirtualCheckupHandoffAuthErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RootAuthHttpInterceptor,
      multi: true,
    },
    { provide: HTTP_INTERCEPTORS, useClass: SSOAuthInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: VirtualCheckupHandoffInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HaleonAuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PortalHttpInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useFactory: (analytics: AnalyticsService) =>
        createPosthogWrappedErrorHandler(
          analytics,
          Sentry.createErrorHandler({
            showDialog: false,
          })
        ),
      deps: [AnalyticsService],
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        return () => {
          // Initialize Sentry tracing
        };
      },
      deps: [Sentry.TraceService],
      multi: true,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(private iconRegistry: IconRegistryService) {
    this.registerIcons();
  }

  private registerIcons() {
    this.iconRegistry.init("../assets/svgs");
    this.iconRegistry.register(SVG_ICON_NAMES);
    this.iconRegistry.init("../assets/");
    this.iconRegistry.register([
      "chevron-right",
      "chevron-left",
      "social-instagram",
      "social-linkedin",
      "social-email",
      "social-map",
    ]);
  }
}
